import { useAuth0 } from '@auth0/auth0-react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useTranslate } from '@growtherapy/i18n';
import { DrawerBody, DrawerFooter, Skeleton, Subhead, cn } from '@growtherapy/sprout-ui';

import {
  SessionIntentionNotificationCadence,
  SessionIntentionNotificationTimeOfDay,
} from '@grow-therapy-team/seedling-components';

import { DrawerHeader } from '../../components/DrawerHeader';
import { logger } from '../../datadog/logger';
import { useGetTelehealthSessionInfo } from '../../hooks';
import { SessionIntention } from '../../hooks/intentions/types';
import { useCreateSessionIntentionMutation } from '../../hooks/intentions/useCreateSessionIntentionMutation';
import {
  CurrentSessionIntention,
  useGetCurrentIntentionQuery,
} from '../../hooks/intentions/useGetCurrentIntentionQuery';
import { useUpdateSessionIntentionMutation } from '../../hooks/intentions/useUpdateSessionIntentionMutation';
import { useTheme } from '../../hooks/useTheme';
import { useTrackEvent } from '../../segment/segment';
import { EventTypeEnum } from '../../segment/types';
import { clientUserShortIdAtom } from '../../state';
import { Theme } from '../../state/theme';
import { toast } from '../../toasts';
import { useSendSessionIntentionEditedCallback } from '../messages/useSendSessionIntentionEditedCallback';
import { useSendSessionIntentionPromptAcknowledgedCallback } from '../messages/useSendSessionIntentionPromptAcknowledgedCallback';
import { visitorDrawerStateAtom } from '../state';
import { CompletedIntentionDrawerBody } from './CompletedIntentionDrawerBody';
import { SetIntentionDrawerBody } from './SetIntentionDrawerBody';
import { UnauthenticatedIntentionsDrawer } from './UnauthenticatedIntentionsDrawer';

function IntentionsDrawerSkeleton() {
  return (
    <>
      <DrawerHeader>
        <Skeleton className='w-36' />
      </DrawerHeader>
      <DrawerBody>
        <div className='flex flex-col gap-250' aria-hidden>
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      </DrawerBody>
      <DrawerFooter>
        <div className='flex w-full gap-4 justify-end'>
          <Skeleton className='w-24 h-14 rounded-full m-0' />
        </div>
      </DrawerFooter>
    </>
  );
}

export function IntentionsDrawer() {
  const { isAuthenticated } = useAuth0();

  const t = useTranslate();
  const { theme } = useTheme();
  const isDarkTheme = theme === Theme.DARK;
  const { providerShortId, patientShortId } = useParams();
  const clientUserShortId = useAtomValue(clientUserShortIdAtom);
  const { data, loading } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;

  const sendPromptAcknowledged = useSendSessionIntentionPromptAcknowledgedCallback();
  const [shouldShowCompletedIntention, setShouldShowCompletedIntention] = useState(false);
  const [createSessionIntention, { loading: createIntentionLoading }] = useCreateSessionIntentionMutation();
  const setDrawerState = useSetAtom(visitorDrawerStateAtom);
  const [intention, setIntention] = useState<SessionIntention | CurrentSessionIntention | null>(null);
  const [updateSessionIntention, { loading: editIntentionLoading }] = useUpdateSessionIntentionMutation();
  const sendIntentionEdited = useSendSessionIntentionEditedCallback();
  const { userViewed } = useTrackEvent();

  const { loading: intentionLoading, refetch } = useGetCurrentIntentionQuery(
    clientUserShortId ?? '',
    providerShortId ?? '',
    appointmentShortId,
    {
      skip: !appointmentShortId,
      onCompleted: (data) => {
        setIntention(data?.intentionHistory?.currentSessionIntention ?? null);
        setShouldShowCompletedIntention(true);
      },
    },
  );

  useEffect(
    function trackIntentionViewed() {
      if (appointmentShortId && !intentionLoading) {
        const intentionStatus = intention ? 'existing' : 'new';
        userViewed(EventTypeEnum.INTENTION_MODULE_VIEWED, {
          appointmentShortId,
          isClientUserAuthenticated: isAuthenticated,
          intentionStatus,
        });
      }
    },
    // We only want to track on drawer open
    // when appointmentShortId is available and we're no longer loading intention
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [appointmentShortId, intentionLoading],
  );

  const handleSetIntention = async (
    intention: string,
    notificationCadence: SessionIntentionNotificationCadence | undefined,
    notificationTimeOfDay: SessionIntentionNotificationTimeOfDay | undefined,
  ) => {
    if (!patientShortId || !appointmentShortId) {
      logger.error('Missing required IDs for creating session intention', { patientShortId, appointmentShortId });
      return;
    }

    try {
      const result = await createSessionIntention({
        variables: {
          clientUserShortId,
          appointmentShortId,
          contentText: intention,
          notificationCadence,
          notificationTimeOfDay,
        },
      });

      setIntention(result.data?.createSessionIntention?.sessionIntention ?? null);
      await sendPromptAcknowledged();
      setShouldShowCompletedIntention(true);
    } catch (error) {
      logger.error('Error setting session intention', { error });
      toast.error(t('telehealth-core:sessionIntention.couldNotSetIntention'));
    }
  };

  const handleUpdateIntention = async (
    contentText: string,
    notificationCadence?: SessionIntentionNotificationCadence,
    notificationTimeOfDay?: SessionIntentionNotificationTimeOfDay,
  ) => {
    if (!intention) {
      return;
    }
    try {
      const result = await updateSessionIntention({
        variables: {
          sessionIntentionShortId: intention.shortId,
          contentText,
          // intentionally set these to null if not defined (aka no reminders)
          notificationCadence: notificationCadence ?? null,
          notificationTimeOfDay: notificationTimeOfDay ?? null,
        },
      });

      const sessionIntention = result.data?.updateSessionIntention?.sessionIntention;
      if (sessionIntention) {
        setIntention(sessionIntention);
        sendIntentionEdited(sessionIntention.shortId);
        setShouldShowCompletedIntention(true);
      }
    } catch (error) {
      logger.error('Error editing session intention', { error });
      toast.error(t('telehealth-core:sessionIntention.couldNotEditIntention'));
    }
  };

  if (!isAuthenticated) {
    return <UnauthenticatedIntentionsDrawer onRefetch={refetch} />;
  }

  if (loading || intentionLoading) {
    return <IntentionsDrawerSkeleton />;
  }

  return (
    <>
      <DrawerHeader>
        <div className='flex items-center'>
          <Subhead.Small className={cn({ 'rebrand:text-text-inverse': isDarkTheme })}>
            {t('telehealth-core:intentions.intention')}
          </Subhead.Small>
        </div>
      </DrawerHeader>
      {shouldShowCompletedIntention && intention ? (
        <CompletedIntentionDrawerBody
          isDarkTheme={isDarkTheme}
          onClickEdit={() => setShouldShowCompletedIntention(false)}
          onClickDone={() => setDrawerState(null)}
          intention={intention}
          appointmentShortId={appointmentShortId}
        />
      ) : (
        <SetIntentionDrawerBody
          onClickBack={() => setDrawerState(null)}
          onClickNext={intention ? handleUpdateIntention : handleSetIntention}
          isDarkTheme={isDarkTheme}
          loading={editIntentionLoading || createIntentionLoading}
          intention={intention}
          appointmentShortId={appointmentShortId}
        />
      )}
    </>
  );
}
